import React, { useState, useEffect } from "react";
import LayoutContent from "../components/LayoutContent";
import DecorativeLines2 from "../components/DecorativeLines2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSyncAlt,
    faCheck,
    faCheckDouble,
    faCogs,
} from "@fortawesome/free-solid-svg-icons";
import { getStoredCategories, setStoredCategories } from "../utils/storage";
import axios from "axios";

const QuestionSelect = ({ onComplete }) => {
    const logo_header = window.themes?.logo || window.welcome?.logo_welcome;
    const welcome_title =
        window.welcome?.title_welcome || "ASML RBA Security Leadership";
    const welcome_subtitle = window.welcome?.subtitle_welcome || "WELCOME TO";
    const next_button_text = window.welcome?.next_button_text || "Start Game";
    const header_texts = window.welcome?.header_texts || {
        selected_sectors: "Selected sectors",
        total_selected_sectors: "selected",
        reset: "Reset",
        select_all: "Select All",
    };
    const question_sectors = window.question_sectors
        ? window.question_sectors.map((sector) => ({
              sector_id: sector.sector_id,
              sector_name: sector.sector_name,
              thumbnail: sector.thumbnail,
          }))
        : [];

    const user = window.themes?.user;

    // Initial selected items from storage or matching default
    const [selectedIds, setSelectedIds] = useState(() =>
        getStoredCategories([]),
    );

    useEffect(() => {
        setStoredCategories(selectedIds);
    }, [selectedIds]);

    const toggleSelect = (sector_id) => {
        setSelectedIds((prev) =>
            prev.includes(sector_id)
                ? prev.filter((item) => item !== sector_id)
                : [...prev, sector_id],
        );
    };

    const handleReset = () => {
        setSelectedIds([]);
    };

    const handleSelectAll = () => {
        setSelectedIds(question_sectors.map((sector) => sector.sector_id));
    };

    const handleCreateGame = () => {
        if (selectedIds.length === 0) return;

        const baseUrl = window.themeObj.restURL;
        const apiURL = `${baseUrl}/game/create-game`;
        axios
            .post(apiURL)
            .then((response) => {
                const gpID = response?.data?.data?.game_progress_id;
                window.themes.user.current_game_progress = gpID;

                setStoredCategories(selectedIds);
                if (onComplete) {
                    onComplete(selectedIds);
                }
            })
            .catch(console.error);
    };

    return (
        <LayoutContent>
            <div className="QuestionSelect">
                {/* Reusable Sliced Decorative Accent Lines Component Variant 2 */}
                <DecorativeLines2 variant="question-select" label="SELECTION" />

                {/* Left Panel: 5 Box Rows */}
                <div className="QuestionSelect__left">
                    {/* Box 1 (Top): Logo */}
                    <div className="QuestionSelect__box QuestionSelect__box--1">
                        <div className="QuestionSelect__logo">
                            {logo_header ? (
                                <img
                                    src={logo_header}
                                    alt="Logo"
                                    className="QuestionSelect__logo--img"
                                />
                            ) : (
                                <span className="QuestionSelect__logo--text">
                                    ASML
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Box 2: Empty Spacer Row */}
                    <div className="QuestionSelect__box QuestionSelect__box--2" />

                    {/* Box 3: Text Title & Subtitle */}
                    <div className="QuestionSelect__box QuestionSelect__box--3">
                        <div className="QuestionSelect__welcome">
                            <span className="QuestionSelect__subtitle">
                                {welcome_subtitle}
                            </span>
                            <h1 className="QuestionSelect__title">
                                {welcome_title}
                            </h1>
                        </div>
                    </div>

                    {/* Box 4: User details (Jerrel van Hecke to Sectornaam) */}
                    <div className="QuestionSelect__box QuestionSelect__box--4">
                        <div className="QuestionSelect__user-card">
                            <div className="QuestionSelect__user-info">
                                <h2 className="QuestionSelect__user-name">
                                    {user?.full_name || "Jerrel van Hecke"}
                                </h2>
                                <p
                                    className="QuestionSelect__user-role"
                                    style={{ display: "none" }}
                                >
                                    {user?.user_role || "Regional Manager"}
                                </p>
                            </div>
                            <div
                                className="QuestionSelect__user-sector"
                                style={{ display: "none" }}
                            >
                                <FontAwesomeIcon
                                    icon={faCogs}
                                    className="QuestionSelect__sector-icon"
                                />
                                <span>{user?.sector || "Sectornaam"}</span>
                            </div>
                        </div>
                    </div>

                    {/* Box 5: Start Game Button */}
                    <div className="QuestionSelect__box QuestionSelect__box--5">
                        <button
                            type="button"
                            className="QuestionSelect__start-btn"
                            onClick={handleCreateGame}
                            disabled={selectedIds.length === 0}
                        >
                            {next_button_text}
                        </button>
                    </div>
                </div>

                {/* Right Panel (Spans 6 Boxes: Cell 6 - 11, Scrollable) */}
                <div className="QuestionSelect__right">
                    <div className="QuestionSelect__header">
                        {/* 2 boxes for Title */}
                        <div className="QuestionSelect__header-item QuestionSelect__header-item--title">
                            <h2 className="QuestionSelect__header-title">
                                {header_texts.selected_sectors}
                            </h2>
                        </div>

                        {/* 1 box for Count */}
                        <div className="QuestionSelect__header-item QuestionSelect__header-item--count">
                            <span className="QuestionSelect__header-count">
                                {selectedIds.length}{" "}
                                {header_texts.total_selected_sectors}
                            </span>
                        </div>

                        {/* 1 box for Spacer */}
                        <div className="QuestionSelect__header-item QuestionSelect__header-item--spacer" />

                        {/* 1 box for Reset */}
                        <div className="QuestionSelect__header-item QuestionSelect__header-item--reset">
                            <button
                                type="button"
                                className="QuestionSelect__action-btn QuestionSelect__action-btn--reset"
                                onClick={handleReset}
                                disabled={selectedIds.length === 0}
                            >
                                <FontAwesomeIcon
                                    icon={faSyncAlt}
                                    className="QuestionSelect__action-icon"
                                />
                                <span>{header_texts.reset}</span>
                            </button>
                        </div>

                        {/* 1 box for Select All */}
                        <div className="QuestionSelect__header-item QuestionSelect__header-item--select-all">
                            <button
                                type="button"
                                className="QuestionSelect__action-btn QuestionSelect__action-btn--select-all"
                                onClick={handleSelectAll}
                            >
                                <FontAwesomeIcon
                                    icon={faCheckDouble}
                                    className="QuestionSelect__action-icon"
                                />
                                <span>{header_texts.select_all}</span>
                            </button>
                        </div>
                    </div>

                    <div className="QuestionSelect__grid">
                        {question_sectors.map((sector) => {
                            const isSelected = selectedIds.includes(
                                sector.sector_id,
                            );
                            return (
                                <div
                                    key={sector.sector_id}
                                    className="QuestionSelect__cell"
                                >
                                    <div
                                        className={`QuestionSelect__card ${
                                            isSelected
                                                ? "QuestionSelect__card--selected"
                                                : ""
                                        }`}
                                        onClick={() =>
                                            toggleSelect(sector.sector_id)
                                        }
                                        style={{
                                            backgroundImage: `url(${sector.thumbnail})`,
                                        }}
                                    >
                                        <div className="QuestionSelect__card-overlay" />
                                        <div
                                            className={`QuestionSelect__card-badge ${
                                                isSelected ? "is-active" : ""
                                            }`}
                                        >
                                            {isSelected ? (
                                                <FontAwesomeIcon
                                                    icon={faCheck}
                                                    className="badge-icon"
                                                />
                                            ) : (
                                                <span className="badge-unselected-dot" />
                                            )}
                                        </div>
                                        <div className="QuestionSelect__card-title">
                                            {sector.sector_name}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </LayoutContent>
    );
};

export default QuestionSelect;
